import React, { useState, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Textarea,
  FormControl,
  FormLabel,
  ButtonGroup,
  Grid,
  Card,
  CardHeader,
  CardBody,
  IconButton,
  Flex,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Tag,
  TagLabel,
  Code,
  Heading,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Link,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon, EditIcon, StarIcon, CheckIcon, WarningIcon, AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { FiRefreshCw } from 'react-icons/fi';
import { useFindMany, useAction, useGlobalAction, useUser } from '@gadgetinc/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { hasRole, normalizeRoles } from '../lib/roleUtils';
import { useSemesterFilter } from "../hooks/useSemesterFilter";
import SemesterFilterDropdown from "../components/SemesterFilterDropdown";

// Helper to parse AI evaluation response from markdown JSON
const parseAIEvaluation = (record) => {
  // The aiEvaluationResponse is a richText field, need to get the markdown property
  if (!record?.aiEvaluationResponse) return null;
  
  // Get the markdown content
  const markdown = record.aiEvaluationResponse.markdown;
  if (!markdown) return null;
  
  try {
    // Parse the JSON from the markdown
    const parsed = JSON.parse(markdown);
    return parsed;
  } catch (e) {
    console.error('Failed to parse AI evaluation JSON:', e);
    console.error('Raw markdown:', markdown);
    return null;
  }
};

// Helper to get grade color scheme
const getGradeColorScheme = (grade) => {
  if (!grade) return 'gray';
  const lowerGrade = grade.toLowerCase();
  if (lowerGrade === 'pass') return 'green';
  if (lowerGrade === 'fail') return 'red';
  if (lowerGrade === 'partial') return 'yellow';
  if (lowerGrade === 'missing') return 'gray';
  return 'blue';
};

// Helper to get pass status color scheme
const getPassStatusColorScheme = (status) => {
  if (!status) return 'gray';
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === 'pass') return 'green';
  if (lowerStatus === 'fail') return 'red';
  if (lowerStatus === 'needs_review') return 'yellow';
  return 'blue';
};

// Helper to extract patient count from various data sources
const getPatientCount = (record) => {
  // Parse AI evaluation first
  const aiEval = parseAIEvaluation(record);
  if (aiEval?.shift?.document_info?.total_patients) {
    return aiEval.shift.document_info.total_patients;
  }
  
  // Fallback to structuredData
  if (record?.structuredData?.patients?.length) {
    return record.structuredData.patients.length;
  }
  
  // Fallback to patientEncounterCount
  if (record?.patientEncounterCount) {
    return record.patientEncounterCount;
  }
  
  return 0;
};


// Helper to extract shift location from various data sources
const getShiftLocation = (record) => {
  // Parse AI evaluation first
  const aiEval = parseAIEvaluation(record);
  if (aiEval?.shift?.location) {
    return aiEval.shift.location;
  }
  
  // Fallbacks
  if (record?.fisdapData?.location) return record.fisdapData.location;
  if (record?.structuredData?.shift_location) return record.structuredData.shift_location;
  if (record?.emailMetadata?.location) return record.emailMetadata.location;
  
  return 'Unknown';
};

// Helper to extract student's cohort from cohortMembership records
const getStudentCohort = (submittedBy) => {
  if (!submittedBy?.cohortMemberships?.edges) {
    return null;
  }
  
  // Find the first active student membership
  const studentMembership = submittedBy.cohortMemberships.edges
    .map(edge => edge.node)
    .find(m => m.active && m.primaryRole === 'student' && m.cohort);
  
  return studentMembership?.cohort || null;
};

// Extract shift date from evaluation data
const getShiftDate = (shift) => {
  const shiftDate = shift.aiEvaluationMetadata?.shift?.document_info?.shift_date ||
                    shift.structuredData?.shift?.shift_date ||
                    shift.structuredData?.shift?.document_info?.shift_date;
  
  if (shiftDate) {
    try {
      return new Date(shiftDate).toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      });
    } catch {
      return shiftDate;
    }
  }
  return 'Unknown';
};

// Check if shift is locked
const isShiftLocked = (shift) => {
  return shift.structuredData?.shift?.locked === true ||
         shift.structuredData?.shift?.shift_locked === true;
};

// Helper to check if a file URL is a PDF
const isPDF = (url) => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  return urlLower.endsWith('.pdf') || 
         urlLower.includes('.pdf?') || 
         urlLower.includes('content-type=application') || 
         urlLower.includes('/pdf') ||
         urlLower.includes('mime');
};

// Helper to get document URL from file field
const getFileURL = (fileField) => {
  if (!fileField) return null;
  if (typeof fileField === 'string') return fileField;
  if (fileField.url) return fileField.url;
  console.log('File field structure:', fileField);
  return null;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatSemesterBadge = (semester) => {
  if (!semester) return "All semesters";
  const start = toDateInputValue(semester.startDate);
  const end = toDateInputValue(semester.endDate);
  if (start && end) return `${semester.name} (${start} to ${end})`;
  return semester.name || "Selected semester";
};

const HITLReview = () => {
  const toast = useToast();
  const user = useUser();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isReprocessDialogOpen, onOpen: onReprocessDialogOpen, onClose: onReprocessDialogClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  // Check if user has admin access
  const hasAdminAccess = hasRole(user, 'admin') || hasRole(user, 'system-admin');

  // Query user's cohort memberships for FISDAP review access
  const [{ data: userMemberships, fetching: loadingMemberships }] = useFindMany(api.cohortMembership, {
    filter: { 
      userId: { equals: user?.id },
      active: { equals: true },
      hasFisdapReviewAccess: { equals: true }
    },
    select: {
      id: true,
      cohort: {
        id: true,
        institution: true,
        displayName: true
      }
    },
    pause: !user?.id
  });

  // Extract allowed institutions from memberships
  const allowedInstitutions = useMemo(() => {
    if (!userMemberships) return [];
    return userMemberships
      .map(m => m.cohort?.institution)
      .filter(Boolean);
  }, [userMemberships]);
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('');
  const [shiftTypeFilter, setShiftTypeFilter] = useState('');
  const [hideDrafts, setHideDrafts] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [semesterDateRange, setSemesterDateRange] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [reviewData, setReviewData] = useState({
    auditVerdict: '',
    overallGrade: 3,
    reviewNotes: '',
    fieldOverrides: {}
  });
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [commentingField, setCommentingField] = useState(null);
  const [fieldComment, setFieldComment] = useState('');
  const [showDocumentView, setShowDocumentView] = useState(false);
  const [documentViewers, setDocumentViewers] = useState({});
  const [patientFlags, setPatientFlags] = useState({});
  const [expandedPatients, setExpandedPatients] = useState(new Set());
  const [loadingDocuments, setLoadingDocuments] = useState(new Set());
  const {
    semesters,
    selectedSemesterId,
    setSelectedSemesterId,
    semesterDateRange: selectedSemesterDateRange,
    loading: semestersLoading,
  } = useSemesterFilter(selectedCohortId);
  const selectedSemester = useMemo(
    () => semesters.find((s) => s.id === selectedSemesterId) || null,
    [semesters, selectedSemesterId]
  );

  const [{ data: allCohorts }] = useFindMany(api.cohort, {
    filter: { isActive: { equals: true } },
    select: {
      id: true,
      institution: true,
      displayName: true,
      name: true,
    },
    sort: { displayName: "Ascending" },
    pause: !hasAdminAccess,
  });

  const cohortOptions = useMemo(() => {
    if (hasAdminAccess) {
      return allCohorts || [];
    }
    if (!userMemberships) return [];
    const dedup = new Map();
    for (const membership of userMemberships) {
      const cohort = membership?.cohort;
      if (cohort?.id) dedup.set(cohort.id, cohort);
    }
    return Array.from(dedup.values()).sort((a, b) =>
      String(a.displayName || a.name || "").localeCompare(String(b.displayName || b.name || ""))
    );
  }, [hasAdminAccess, allCohorts, userMemberships]);

  React.useEffect(() => {
    if (selectedSemesterId && selectedSemesterDateRange) {
      setSemesterDateRange({
        startDate: toDateInputValue(selectedSemesterDateRange.startDate),
        endDate: toDateInputValue(selectedSemesterDateRange.endDate),
      });
    }
  }, [selectedSemesterId, selectedSemesterDateRange]);

  // Build filters for shift processing records
  const buildFilters = () => {
    const filters = [];

    if (statusFilter === 'needs_review') {
      filters.push({ requiresReview: { equals: true } });
    } else if (statusFilter === 'completed') {
      filters.push({ 
        AND: [
          { requiresReview: { equals: false } },
          { auditVerdict: { notEquals: 'required' } }
        ]
      });
    }

    if (verdictFilter) {
      filters.push({ auditVerdict: { equals: verdictFilter } });
    }

    if (shiftTypeFilter) {
      filters.push({ shiftType: { equals: shiftTypeFilter } });
    }

    if (selectedCohortId) {
      filters.push({ studentCohortId: { equals: selectedCohortId } });
    }

    if (semesterDateRange?.startDate || semesterDateRange?.endDate) {
      const submissionDate = {};
      if (semesterDateRange?.startDate) {
        submissionDate.greaterThanOrEqual = new Date(`${semesterDateRange.startDate}T00:00:00`).toISOString();
      }
      if (semesterDateRange?.endDate) {
        submissionDate.lessThanOrEqual = new Date(`${semesterDateRange.endDate}T23:59:59.999`).toISOString();
      }
      filters.push({ submissionDate });
    }

    // Add institution filter based on user's review access
    if (!hasAdminAccess && allowedInstitutions.length > 0) {
      filters.push({ 
        submittedBy: { 
          institution: { in: allowedInstitutions } 
        }
      });
    }

    // Add draft filter if hideDrafts is enabled
    if (hideDrafts) {
      filters.push({ isDraft: { notEquals: true } });
    }

    return filters.length > 0 ? { AND: filters } : {};
  };

  // API hooks
  const [{ data: shiftRecords, fetching: loadingRecords, error: recordsError }, refetchRecords] = useFindMany(api.shiftProcessing, {
    first: 250,
    sort: { createdAt: 'Descending' },
    filter: buildFilters(),
    select: {
      id: true,
      sourceShiftId: true,
      submissionEmail: true,
      originalFilename: true,
      originalFile: { url: true, fileName: true, mimeType: true },
      createdAt: true,
      auditVerdict: true,
      requiresReview: true,
      evaluationStatus: true,
      aiEvaluationMetadata: true,
      aiEvaluationResponse: { markdown: true },
      patientEncounterCount: true,
      reviewedAt: true,
      reviewedBy: { firstName: true, lastName: true },
      structuredData: true,
      emailMetadata: true,
      fisdapData: true,
      isDraft: true,
      shiftType: true,
      userFile: {
        id: true,
        title: true,
        originalFilename: true,
        file: { url: true, fileName: true, mimeType: true }
      },
      submittedBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        institution: true,
        cohortMemberships: {
          edges: {
            node: {
              id: true,
              active: true,
              primaryRole: true,
              cohort: {
                id: true,
                name: true,
                displayName: true
              }
            }
          }
        }
      },
      clinicalEvaluations: {
        edges: {
          node: {
            id: true,
            passStatus: true,
            parsedOutput: true,
            needsHitl: true
          }
        }
      }
    }
  });

  const [{ data: updateResult, fetching: updating, error: updateError }, updateShiftProcessing] = useAction(api.shiftProcessing.update);
  const [{ data: notificationResult, fetching: sendingNotification }, sendNotification] = useGlobalAction(api.sendEvaluationNotificationA);
  const [{ data: reprocessResult, fetching: reprocessing, error: reprocessError }, reprocessShift] = useGlobalAction(api.reprocessShift);

  // Client-side search because Gadget StringFilter doesn't support `contains` for these fields.
  const filteredShiftRecords = useMemo(() => {
    if (!shiftRecords) return [];
    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) return shiftRecords;

    return shiftRecords.filter((record) => {
      const submittedByName = record?.submittedBy
        ? `${record.submittedBy.firstName || ""} ${record.submittedBy.lastName || ""}`.trim()
        : "";
      const haystack = [
        String(record?.sourceShiftId || ""),
        String(record?.submissionEmail || ""),
        String(record?.originalFilename || ""),
        submittedByName,
      ].join(" ").toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [shiftRecords, searchQuery]);

  // Fetch all userFiles related to the selected shift
  const [{ data: relatedUserFiles, fetching: loadingUserFiles }] = useFindMany(
    api.userFile,
    {
      filter: selectedRecord?.id ? {
        shiftProcessingId: { equals: selectedRecord.id },
        status: { equals: 'active' }
      } : { id: { equals: 'never' } }, // dummy filter when no record selected
      select: {
        id: true,
        title: true,
        originalFilename: true,
        file: { url: true, fileName: true, mimeType: true },
        fileSizeBytes: true
      },
      pause: !selectedRecord?.id // don't fetch until we have a record
    }
  );

  // Memoized statistics
  const stats = useMemo(() => {
    if (!filteredShiftRecords) return { total: 0, needsReview: 0, completed: 0, passRate: 0 };
    
    const total = filteredShiftRecords.length;
    const needsReview = filteredShiftRecords.filter(record => record.requiresReview || record.auditVerdict === 'required').length;
    const completed = total - needsReview;
    const passed = filteredShiftRecords.filter(record => record.auditVerdict === 'pass').length;
    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

    return { total, needsReview, completed, passRate };
  }, [filteredShiftRecords]);

  // Handle record selection and modal opening
  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setActiveTab(0);
    
    // Initialize review data from existing record
    setReviewData({
      auditVerdict: record.auditVerdict || '',
      overallGrade: 3,
      reviewNotes: '',
      fieldOverrides: {}
    });
    
    setSelectedFields(new Set());
    setCommentingField(null);
    setFieldComment('');
    setDocumentViewers({});
    setPatientFlags({});
    setExpandedPatients(new Set());
    setLoadingDocuments(new Set());
    
    onOpen();
  };

  // Handle reprocess shift
  const handleReprocessShift = async () => {
    if (!selectedRecord) return;
    
    try {
      const result = await reprocessShift({
        shiftProcessingId: selectedRecord.id
      });
      
      // Close dialog IMMEDIATELY regardless of result to prevent lingering
      onReprocessDialogClose();
      
      // Then handle the result
      if (result?.success && result?.newShiftId) {
        toast({
          title: 'Reprocessing Started',
          description: `New shift processing created with ID: ${result.newShiftId}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Close the main modal and refresh
        onClose();
        refetchRecords();
      } else if (result?.errors?.length > 0) {
        toast({
          title: 'Reprocessing Failed',
          description: result.errors[0].message || 'Failed to reprocess shift',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // No success flag and no errors - unexpected state
        toast({
          title: 'Reprocessing Status Unknown',
          description: 'The reprocessing may have started but the status is unclear. Check the records list.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      // Close dialog even on error
      onReprocessDialogClose();
      
      toast({
        title: 'Reprocessing Failed',
        description: error.message || 'Failed to reprocess shift',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle review submission
  const handleSubmitReview = async () => {
    // Validation...
    const hasReviewContent = reviewData.reviewNotes || 
      Object.keys(reviewData.fieldOverrides).length > 0;
    
    if (!selectedRecord) return;
    
    if (!reviewData.auditVerdict && !hasReviewContent) {
      toast({
        title: 'Validation Error',
        description: 'Please either select a verdict or add review notes/comments',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    let updateSucceeded = false;
    let notificationSucceeded = false;

    try {
      // STEP 1: Save the review data (this is critical and must succeed)
      const updateData = {
        id: selectedRecord.id,
        reviewedAt: new Date(),
        reviewedBy: { _link: user?.id },
        structuredData: {
          ...selectedRecord.structuredData,
          humanReview: {
            overallGrade: reviewData.overallGrade,
            reviewNotes: reviewData.reviewNotes,
            fieldOverrides: reviewData.fieldOverrides,
            patientFlags: patientFlags,
            reviewedAt: new Date().toISOString(),
            reviewedBy: user?.id
          }
        }
      };
      
      // Only update verdict and requiresReview if verdict is provided
      if (reviewData.auditVerdict) {
        updateData.auditVerdict = reviewData.auditVerdict;
        updateData.requiresReview = false;
      }
      
      await updateShiftProcessing(updateData);
      updateSucceeded = true;

      // STEP 2: Try to send notifications (non-critical, don't block on failure)
      if (reviewData.auditVerdict) {
        try {
          await sendNotification({
            shiftProcessingId: selectedRecord.id,
            forceNotification: true
          });
          notificationSucceeded = true;
        } catch (notificationError) {
          console.error('Notification failed but review was saved:', notificationError);
          // Don't throw - review is already saved
        }
      }

      // STEP 3: Show appropriate success message
      if (reviewData.auditVerdict) {
        if (notificationSucceeded) {
          toast({
            title: 'Review Submitted',
            description: 'Your review has been saved and notifications sent successfully',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Review Saved',
            description: 'Your review was saved successfully, but notifications may have failed. You may need to notify instructors manually.',
            status: 'warning',
            duration: 6000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Review Saved',
          description: 'Your review notes have been saved',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // STEP 4: Close modal and refresh list (always do this if save succeeded)
      onClose();
      refetchRecords();

    } catch (error) {
      // This only catches errors from the update itself
      if (!updateSucceeded) {
        toast({
          title: 'Save Failed',
          description: error.message || 'Failed to save review. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Handle field comment
  const handleFieldComment = (fieldPath, comment, grade) => {
    setReviewData(prev => ({
      ...prev,
      fieldOverrides: {
        ...prev.fieldOverrides,
        [fieldPath]: {
          comment,
          grade,
          timestamp: new Date().toISOString(),
          reviewerId: user?.id
        }
      }
    }));
  };

  // Generate training data from selected fields
  const handleGenerateTraining = () => {
    if (selectedFields.size === 0) {
      toast({
        title: 'No Fields Selected',
        description: 'Please select fields to generate training data',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Training Data Generated',
      description: `Generated training data for ${selectedFields.size} fields`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Star rating component
  const StarRating = ({ value, onChange, disabled = false }) => (
    <HStack spacing={1}>
      {[1, 2, 3, 4, 5].map((star) => (
        <IconButton
          key={star}
          icon={<StarIcon />}
          size="sm"
          variant="ghost"
          colorScheme={star <= value ? "yellow" : "gray"}
          color={star <= value ? "yellow.400" : "gray.300"}
          onClick={() => !disabled && onChange && onChange(star)}
          isDisabled={disabled}
          aria-label={`${star} star${star !== 1 ? 's' : ''}`}
        />
      ))}
    </HStack>
  );

  // JSON field display with click-to-comment
  const JSONFieldDisplay = ({ data, path = '', level = 0 }) => {
    if (!data || typeof data !== 'object') {
      return (
        <Box 
          as="span" 
          cursor="pointer"
          _hover={{ bg: 'gray.600' }}
          p={1}
          borderRadius="sm"
          onClick={() => {
            setCommentingField(`${path}`);
            setFieldComment(reviewData.fieldOverrides[path]?.comment || '');
          }}
        >
          <Code colorScheme="blue">{JSON.stringify(data)}</Code>
        </Box>
      );
    }

    return (
      <Box pl={level * 4}>
        {Object.entries(data).map(([key, value]) => {
          const currentPath = path ? `${path}.${key}` : key;
          const hasOverride = reviewData.fieldOverrides[currentPath];
          
          return (
            <Box key={key} mb={1}>
              <HStack spacing={2} align="start">
                <Text 
                  fontWeight="bold" 
                  color="purple.300"
                  cursor="pointer"
                  _hover={{ bg: 'gray.600' }}
                  p={1}
                  borderRadius="sm"
                  onClick={() => {
                    setCommentingField(currentPath);
                    setFieldComment(reviewData.fieldOverrides[currentPath]?.comment || '');
                  }}
                >
                  {key}:
                </Text>
                {hasOverride && (
                  <Tooltip label={`Grade: ${hasOverride.grade}/5 - ${hasOverride.comment}`}>
                    <Badge colorScheme="green" size="xs">Reviewed</Badge>
                  </Tooltip>
                )}
                <Checkbox
                  size="sm"
                  isChecked={selectedFields.has(currentPath)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedFields);
                    if (e.target.checked) {
                      newSelected.add(currentPath);
                    } else {
                      newSelected.delete(currentPath);
                    }
                    setSelectedFields(newSelected);
                  }}
                  colorScheme="purple"
                />
              </HStack>
              
              {typeof value === 'object' ? (
                <JSONFieldDisplay data={value} path={currentPath} level={level + 1} />
              ) : (
                <Box 
                  pl={4}
                  cursor="pointer"
                  _hover={{ bg: 'gray.600' }}
                  p={1}
                  borderRadius="sm"
                  onClick={() => {
                    setCommentingField(currentPath);
                    setFieldComment(reviewData.fieldOverrides[currentPath]?.comment || '');
                  }}
                >
                  <Code colorScheme="blue">{JSON.stringify(value)}</Code>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Status badge component - updated to check for human reviews
  const StatusBadge = ({ record }) => {
    const verdict = record?.auditVerdict;
    const requiresReview = record?.requiresReview;
    const hasHumanReview = record?.structuredData?.humanReview;
    const hasReviewNotes = record?.reviewedAt || hasHumanReview;
  
    // If reviewed (even without verdict), show that
    if (hasReviewNotes && !verdict) {
      return <Badge colorScheme="cyan">Draft Review</Badge>;
    }
  
    if (requiresReview || verdict === 'required') {
      return <Badge colorScheme="yellow">Needs Review</Badge>;
    }
  
    if (verdict === 'pass') return <Badge colorScheme="green">Pass</Badge>;
    if (verdict === 'fail') return <Badge colorScheme="red">Fail</Badge>;
    if (verdict === 'needs_review') return <Badge colorScheme="orange">Needs Review</Badge>;
  
    return <Badge colorScheme="blue">Pending</Badge>;
  };

  // Check for permission errors
  if (!hasAdminAccess && (!userMemberships || userMemberships.length === 0) && !loadingMemberships) {
    return (
      <Box p={8} bg="gray.900" minH="100vh" color="white">
        <VStack spacing={6} align="center" justify="center" minH="60vh">
          <WarningIcon boxSize={16} color="yellow.400" />
          <Heading size="lg">Access Denied</Heading>
          <Text fontSize="lg" textAlign="center" maxW="600px" color="gray.300">
            You don't have permission to access the FISDAP review tool.
          </Text>
          <Text fontSize="md" textAlign="center" maxW="600px" color="gray.400">
            Contact your institution administrator to request FISDAP review access for your cohort.
          </Text>
          <Button colorScheme="blue" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </VStack>
      </Box>
    );
  }

  if (recordsError) {
    return (
      <Box p={8} bg="gray.900" minH="100vh" color="white">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error Loading Data</AlertTitle>
          <AlertDescription>{recordsError.message}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box bg="gray.900" minH="100vh" color="white" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Back Button */}
        <Box>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            colorScheme="gray"
            onClick={() => navigate('/')}
            size="sm"
          >
            Back to Dashboard
          </Button>
        </Box>

        {/* Navigation Links */}
        <Box>
          <Button
            variant="outline"
            colorScheme="blue"
            onClick={() => navigate('/fisdap-reports')}
            size="sm"
          >
            View FISDAP Reports
          </Button>
        </Box>

        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <HStack spacing={3} align="center">
              <Text fontSize="3xl" fontWeight="bold">
                Clinical Evaluation Review
              </Text>
              <Button
                leftIcon={<ViewIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={() => navigate('/fisdap-reports')}
                size="sm"
              >
                View Reports
              </Button>
              {!hasAdminAccess && allowedInstitutions.length > 0 && (
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.500">Review Access:</Text>
                  <HStack spacing={2}>
                    {allowedInstitutions.map((institution, idx) => (
                      <Badge key={idx} colorScheme="green" fontSize="xs">
                        {institution}
                      </Badge>
                    ))}
                  </HStack>
                </VStack>
              )}
            </HStack>
            <Text color="gray.400">
              Human-in-the-Loop review interface for AI-processed shift evaluations
            </Text>
          </VStack>
          
          {/* Statistics */}
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.300">Selected semester:</Text>
            <Badge colorScheme={selectedSemester ? "green" : "gray"}>
              {formatSemesterBadge(selectedSemester)}
            </Badge>
          </HStack>
          <HStack spacing={4}>
            <Stat textAlign="center">
              <StatNumber fontSize="2xl">{stats.total}</StatNumber>
              <StatLabel>Total Records</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="2xl" color="yellow.400">{stats.needsReview}</StatNumber>
              <StatLabel>Needs Review</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="2xl" color="green.400">{stats.completed}</StatNumber>
              <StatLabel>Completed</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="2xl" color="blue.400">{stats.passRate}%</StatNumber>
              <StatLabel>Pass Rate</StatLabel>
            </Stat>
          </HStack>
        </HStack>

        {/* Search and Filters */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Grid templateColumns="1fr 180px 180px 180px 180px 220px 150px" gap={4} alignItems="end">
                <FormControl>
                  <FormLabel>Search</FormLabel>
                  <Input
                    placeholder="Search by shift ID, email, or filename..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg="gray.700"
                    borderColor="gray.600"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Cohort</FormLabel>
                  <Select
                    value={selectedCohortId}
                    onChange={(e) => {
                      setSelectedCohortId(e.target.value);
                      setSelectedSemesterId("");
                      setSemesterDateRange(null);
                    }}
                    bg="gray.700"
                    borderColor="gray.600"
                  >
                    <option value="">All</option>
                    {cohortOptions.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.displayName || cohort.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Semester</FormLabel>
                  <SemesterFilterDropdown
                    cohortId={selectedCohortId}
                    semesters={semesters}
                    selectedSemesterId={selectedSemesterId}
                    loading={semestersLoading}
                    onChange={(id, range) => {
                      setSelectedSemesterId(id);
                      if (!id || !range) {
                        setSemesterDateRange(null);
                        return;
                      }
                      setSemesterDateRange({
                        startDate: toDateInputValue(range.startDate),
                        endDate: toDateInputValue(range.endDate),
                      });
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    bg="gray.700"
                    borderColor="gray.600"
                  >
                    <option value="">All</option>
                    <option value="needs_review">Needs Review</option>
                    <option value="completed">Completed</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Verdict</FormLabel>
                  <Select
                    value={verdictFilter}
                    onChange={(e) => setVerdictFilter(e.target.value)}
                    bg="gray.700"
                    borderColor="gray.600"
                  >
                    <option value="">All</option>
                    <option value="pass">Pass</option>
                    <option value="fail">Fail</option>
                    <option value="needs_review">Needs Review</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Shift Type</FormLabel>
                  <Select
                    placeholder="All Shift Types"
                    value={shiftTypeFilter}
                    onChange={(e) => setShiftTypeFilter(e.target.value)}
                    bg="gray.700"
                    borderColor="gray.600"
                  >
                    <option value="">All</option>
                    <option value="clinical">Clinical</option>
                    <option value="field">Field</option>
                    <option value="hospital">Hospital</option>
                    <option value="lab">Lab</option>
                    <option value="field_internship">Field Internship</option>
                  </Select>
                </FormControl>

                <Button
                  leftIcon={<SearchIcon />}
                  colorScheme="blue"
                  onClick={() => refetchRecords()}
                  isLoading={loadingRecords}
                >
                  Search
                </Button>
              </Grid>
              
              <HStack spacing={4}>
                <Checkbox
                  isChecked={hideDrafts}
                  onChange={(e) => setHideDrafts(e.target.checked)}
                  colorScheme="blue"
                >
                  Hide Drafts
                </Checkbox>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Results Table */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            {loadingRecords ? (
              <Flex justify="center" p={8}>
                <Spinner size="xl" color="blue.400" />
              </Flex>
            ) : filteredShiftRecords?.length > 0 ? (
              <Box overflowX="auto">
                <Table variant="simple" colorScheme="gray">
                <Thead>
                  <Tr>
                    <Th color="gray.300">Shift ID</Th>
                    <Th color="gray.300">Submitted By</Th>
                    <Th color="gray.300">Cohort</Th>
                    <Th color="gray.300">Created</Th>
                    <Th color="gray.300">Shift Date</Th>
                    <Th color="gray.300" width="60px" textAlign="center">🔒</Th>
                    <Th color="gray.300">Location</Th>
                    <Th color="gray.300">Shift Type</Th>
                    <Th color="gray.300">Patients</Th>
                    <Th color="gray.300">Status</Th>
                    <Th color="gray.300">Reviewed By</Th>
                    <Th color="gray.300">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredShiftRecords.map((record) => {
                    const shiftNeedsAttention = record.auditVerdict === 'needs_review' || record.auditVerdict === 'fail';
                    const rowBg = shiftNeedsAttention ? "yellow.950" : undefined;
                    
                    return (
                    <Tr key={record.id} bg={rowBg} _hover={{ bg: shiftNeedsAttention ? "yellow.900" : "gray.700" }}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontFamily="mono" fontWeight="bold">{record.sourceShiftId}</Text>
                          <Text fontSize="xs" color="gray.500">ID: {record.id}</Text>
                        </VStack>
                      </Td>
                      <Td>
                        {record.submittedBy 
                          ? `${record.submittedBy.firstName} ${record.submittedBy.lastName}`
                          : record.submissionEmail
                        }
                      </Td>
                      <Td>
                        {(() => {
                          const cohort = getStudentCohort(record.submittedBy);
                          return cohort ? (
                            <Text fontSize="sm">{cohort.displayName || cohort.name}</Text>
                          ) : (
                            <Text fontSize="sm" color="gray.500">-</Text>
                          );
                        })()}
                      </Td>
                      <Td>
                        {new Date(record.createdAt).toLocaleDateString()}
                      </Td>
                      <Td fontSize="sm">{getShiftDate(record)}</Td>
                      <Td fontSize="sm" textAlign="center">
                        {isShiftLocked(record) ? (
                          <Text color="green.400" fontSize="xl">🔒</Text>
                        ) : (
                          <Text color="yellow.400" fontSize="xl">🔓</Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="sm">{getShiftLocation(record)}</Text>
                      </Td>
                      <Td>
                        <Badge colorScheme="purple">
                          {record.shiftType || 'Unknown'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme="purple" variant="outline">
                          {getPatientCount(record)}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <StatusBadge record={record} />
                          {record.structuredData?.reprocessedTo && (
                            <Badge colorScheme="cyan" fontSize="xs">
                              <Link
                                onClick={() => {
                                  const reprocessedRecord = shiftRecords?.find(
                                    r => r.id === record.structuredData.reprocessedTo
                                  );
                                  if (reprocessedRecord) {
                                    handleViewRecord(reprocessedRecord);
                                  }
                                }}
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline' }}
                              >
                                Reprocessed
                              </Link>
                            </Badge>
                          )}
                          {record.structuredData?.reprocessedFrom && (
                            <Badge colorScheme="orange" fontSize="xs">
                              <Link
                                onClick={() => {
                                  const originalRecord = shiftRecords?.find(
                                    r => r.id === record.structuredData.reprocessedFrom
                                  );
                                  if (originalRecord) {
                                    handleViewRecord(originalRecord);
                                  }
                                }}
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline' }}
                              >
                                Reprocessing of #{record.structuredData.reprocessedFrom.slice(-4)}
                              </Link>
                            </Badge>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        {record.reviewedBy ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="bold">
                              {record.reviewedBy.firstName} {record.reviewedBy.lastName}
                            </Text>
                            {record.reviewedAt && (
                              <Text fontSize="xs" color="gray.500">
                                {new Date(record.reviewedAt).toLocaleDateString()}
                              </Text>
                            )}
                          </VStack>
                        ) : record.structuredData?.humanReview ? (
                          <Text fontSize="sm" color="cyan.400">Review in progress</Text>
                        ) : (
                          <Text fontSize="sm" color="gray.500">Not reviewed</Text>
                        )}
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          leftIcon={<EditIcon />}
                          colorScheme="green"
                          onClick={() => handleViewRecord(record)}
                        >
                          Review
                        </Button>
                      </Td>
                    </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              </Box>
            ) : (
              <Box textAlign="center" py={8}>
                <Text fontSize="lg" color="gray.400">
                  No records found matching your criteria
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Review Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="full">
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="gray.800" color="white" m={4}>
            <ModalHeader borderBottom="1px" borderColor="gray.700">
              <HStack justify="space-between" align="center">
                <HStack spacing={4}>
                  {/* Document View Toggle - moved to left side */}
                  <Tooltip label={showDocumentView ? "Hide Documents" : "Show Documents"}>
                    <Button
                      leftIcon={<ViewIcon />}
                      size="sm"
                      colorScheme={showDocumentView ? "purple" : "gray"}
                      variant={showDocumentView ? "solid" : "outline"}
                      onClick={() => setShowDocumentView(!showDocumentView)}
                    >
                      {showDocumentView ? "Hide" : "View"} Documents
                    </Button>
                  </Tooltip>
                  
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xl" fontWeight="bold">
                      Clinical Evaluation Review - Shift {selectedRecord?.sourceShiftId} (Record #{selectedRecord?.id})
                    </Text>
                    <HStack>
                      <StatusBadge record={selectedRecord} />
                      <Badge colorScheme="blue" variant="outline">
                        {getPatientCount(selectedRecord)} Patients
                      </Badge>
                    </HStack>
                  </VStack>
                </HStack>
                
                {/* Reprocess Button - only for admins */}
                {hasAdminAccess && (
                  <Tooltip label="Reprocess this shift with current timestamp">
                    <IconButton
                      icon={<FiRefreshCw />}
                      colorScheme="orange"
                      variant="outline"
                      size="sm"
                      onClick={onReprocessDialogOpen}
                      isLoading={reprocessing}
                      aria-label="Reprocess shift"
                    />
                  </Tooltip>
                )}
              </HStack>
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody p={0}>
              <HStack spacing={0} align="stretch" h={showDocumentView ? "calc(100vh - 200px)" : "auto"}>
                {/* Document Viewer Panel - Left Side */}
                {showDocumentView && (
                  <Box 
                    w="50%" 
                    borderRight="2px" 
                    borderColor="gray.700" 
                    bg="gray.850"
                    overflowY="auto"
                    p={4}
                  >
                    <VStack spacing={4} align="stretch">
                      <Heading size="md" color="blue.300">Original Documents</Heading>
                      
                      {/* Original File from shiftProcessing */}
                      {selectedRecord?.originalFile && (() => {
                        console.log('Original file:', selectedRecord?.originalFile);
                        const fileURL = getFileURL(selectedRecord.originalFile);
                        const isOriginalPDF = isPDF(fileURL);
                        console.log('File URL:', fileURL, 'Is PDF:', isOriginalPDF);
                        const docKey = 'original';
                        const isExpanded = documentViewers[docKey];
                        
                        return (
                          <Card bg="gray.700" borderColor="gray.600">
                            <CardHeader>
                              <HStack justify="space-between">
                                <Text fontWeight="bold">Original Submission</Text>
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  onClick={() => {
                                    const willExpand = !documentViewers[docKey];
                                    setDocumentViewers(prev => ({
                                      ...prev,
                                      [docKey]: willExpand
                                    }));
                                    if (willExpand) {
                                      setLoadingDocuments(prev => new Set(prev).add(docKey));
                                    }
                                  }}
                                >
                                  {isExpanded ? 'Hide' : 'View'}
                                </Button>
                              </HStack>
                            </CardHeader>
                            {isExpanded && (
                              <CardBody>
                                {isOriginalPDF ? (
                                  <Box position="relative">
                                    {loadingDocuments.has(docKey) && (
                                      <Flex
                                        position="absolute"
                                        top="0"
                                        left="0"
                                        right="0"
                                        bottom="0"
                                        bg="gray.800"
                                        align="center"
                                        justify="center"
                                        zIndex="1"
                                      >
                                        <VStack spacing={3}>
                                          <Spinner size="xl" color="blue.400" thickness="4px" />
                                          <Text color="gray.400">Loading document...</Text>
                                        </VStack>
                                      </Flex>
                                    )}
                                    <Box 
                                      as="iframe"
                                      src={fileURL + '#toolbar=0'}
                                      w="100%"
                                      h="600px"
                                      border="none"
                                      borderRadius="md"
                                      onLoad={() => {
                                        setLoadingDocuments(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(docKey);
                                          return newSet;
                                        });
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <VStack spacing={2}>
                                    <Text fontSize="sm" color="gray.400">
                                      {selectedRecord?.originalFile?.fileName || selectedRecord?.originalFilename || 'Document'}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Non-PDF file - cannot preview in browser
                                    </Text>
                                  </VStack>
                                )}
                              </CardBody>
                            )}
                          </Card>
                        );
                      })()}
                      
                      {/* Related UserFiles */}
                      {relatedUserFiles?.length > 0 && relatedUserFiles.map((userFile, index) => {
                        const fileURL = getFileURL(userFile.file);
                        const isUserFilePDF = isPDF(fileURL);
                        const docKey = `userfile_${index}`;
                        const isExpanded = documentViewers[docKey];
                        
                        return (
                          <Card key={userFile.id} bg="gray.700" borderColor="gray.600">
                            <CardHeader>
                              <HStack justify="space-between">
                                <Text fontWeight="bold">
                                  {userFile.title || userFile.originalFilename || 'Related Document'}
                                </Text>
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  onClick={() => {
                                    const willExpand = !documentViewers[docKey];
                                    setDocumentViewers(prev => ({
                                      ...prev,
                                      [docKey]: willExpand
                                    }));
                                    if (willExpand) {
                                      setLoadingDocuments(prev => new Set(prev).add(docKey));
                                    }
                                  }}
                                >
                                  {isExpanded ? 'Hide' : 'View'}
                                </Button>
                              </HStack>
                            </CardHeader>
                            {isExpanded && (
                              <CardBody>
                                {isUserFilePDF ? (
                                  <Box position="relative">
                                    {loadingDocuments.has(docKey) && (
                                      <Flex
                                        position="absolute"
                                        top="0"
                                        left="0"
                                        right="0"
                                        bottom="0"
                                        bg="gray.800"
                                        align="center"
                                        justify="center"
                                        zIndex="1"
                                      >
                                        <VStack spacing={3}>
                                          <Spinner size="xl" color="blue.400" thickness="4px" />
                                          <Text color="gray.400">Loading document...</Text>
                                        </VStack>
                                      </Flex>
                                    )}
                                    <Box 
                                      as="iframe"
                                      src={fileURL + '#toolbar=0'}
                                      w="100%"
                                      h="600px"
                                      border="none"
                                      borderRadius="md"
                                      onLoad={() => {
                                        setLoadingDocuments(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(docKey);
                                          return newSet;
                                        });
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <VStack spacing={2}>
                                    <Text fontSize="sm" color="gray.400">
                                      {userFile.originalFilename || 'Document'}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Non-PDF file - cannot preview in browser
                                    </Text>
                                  </VStack>
                                )}
                              </CardBody>
                            )}
                          </Card>
                        );
                      })}
                      
                      {!selectedRecord?.originalFile && (!relatedUserFiles || relatedUserFiles.length === 0) && (
                        <Alert status="info">
                          <AlertIcon />
                          <AlertDescription>
                            No documents available for this shift.
                          </AlertDescription>
                        </Alert>
                      )}
                    </VStack>
                  </Box>
                )}
                
                {/* Review Interface - Right Side (or full width when documents hidden) */}
                <Box w={showDocumentView ? "50%" : "100%"} overflowY="auto">
                  <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
                    <TabList borderBottom="1px" borderColor="gray.700" bg="gray.750" px={6}>
                      <Tab>Summary</Tab>
                      <Tab>Clinical Evaluations</Tab>
                      <Tab>Human Review</Tab>
                    </TabList>

                    <TabPanels>
                  {/* Tab 1: Shift Overview */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch" p={4}>
                      <SimpleGrid columns={2} spacing={6}>
                        {/* Student Information */}
                        <Card bg="gray.700" borderColor="gray.600">
                          <CardHeader>
                            <Heading size="md" color="blue.300">Student Information</Heading>
                          </CardHeader>
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Name</Text>
                                <Text fontWeight="bold">
                                  {selectedRecord?.submittedBy 
                                    ? `${selectedRecord.submittedBy.firstName} ${selectedRecord.submittedBy.lastName}`
                                    : 'Unknown'}
                                </Text>
                              </Box>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Email</Text>
                                <Text>{selectedRecord?.submissionEmail || 'N/A'}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Institution</Text>
                                <Text>{selectedRecord?.submittedBy?.institution || 'N/A'}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Cohort</Text>
                                <Text>
                                  {(() => {
                                    const cohort = getStudentCohort(selectedRecord?.submittedBy);
                                    return cohort ? (cohort.displayName || cohort.name) : 'N/A';
                                  })()}
                                </Text>
                              </Box>
                            </VStack>
                          </CardBody>
                        </Card>

                        {/* Shift Details */}
                        <Card bg="gray.700" borderColor="gray.600">
                          <CardHeader>
                            <Heading size="md" color="purple.300">Shift Details</Heading>
                          </CardHeader>
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Shift ID</Text>
                                <Text fontWeight="bold" fontFamily="mono">{selectedRecord?.sourceShiftId}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Date</Text>
                                <Text>{new Date(selectedRecord?.createdAt).toLocaleDateString()}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Location</Text>
                                <Text>{getShiftLocation(selectedRecord)}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="sm" color="gray.400">Patient Count</Text>
                                <Badge colorScheme="purple" fontSize="lg" p={2}>
                                  {getPatientCount(selectedRecord)} Patients
                                </Badge>
                              </Box>
                            </VStack>
                          </CardBody>
                        </Card>
                      </SimpleGrid>

                      {/* Overall AI Verdict */}
                      <Card bg="gray.700" borderColor="gray.600">
                        <CardHeader>
                          <Heading size="md" color="green.300">Overall AI Assessment</Heading>
                        </CardHeader>
                        <CardBody>
                          <HStack spacing={6} justify="space-around">
                            <VStack>
                              <Text fontSize="sm" color="gray.400">Verdict</Text>
                              <StatusBadge 
                                verdict={selectedRecord?.auditVerdict} 
                                requiresReview={selectedRecord?.requiresReview} 
                              />
                            </VStack>
                            <VStack>
                              <Text fontSize="sm" color="gray.400">Evaluation Status</Text>
                              <Badge colorScheme="blue">
                                {selectedRecord?.evaluationStatus || 'pending'}
                              </Badge>
                            </VStack>
                            <VStack>
                              <Text fontSize="sm" color="gray.400">Requires Review</Text>
                              {selectedRecord?.requiresReview ? (
                                <WarningIcon color="yellow.400" boxSize={6} />
                              ) : (
                                <CheckIcon color="green.400" boxSize={6} />
                              )}
                            </VStack>
                          </HStack>
                        </CardBody>
                      </Card>

                      {/* Summary Statistics */}
                      <Card bg="gray.700" borderColor="gray.600">
                        <CardHeader>
                          <Heading size="md" color="orange.300">Summary Statistics</Heading>
                        </CardHeader>
                        <CardBody>
                          <SimpleGrid columns={4} spacing={4}>
                            <Stat textAlign="center">
                              <StatLabel>Total Patients</StatLabel>
                              <StatNumber>{getPatientCount(selectedRecord)}</StatNumber>
                            </Stat>
                            <Stat textAlign="center">
                              <StatLabel>Completed Assessments</StatLabel>
                              <StatNumber>
                                {selectedRecord?.clinicalEvaluations?.edges?.length || 0}
                              </StatNumber>
                            </Stat>
                            <Stat textAlign="center">
                              <StatLabel>Flagged Items</StatLabel>
                              <StatNumber color="yellow.400">
                                {selectedRecord?.clinicalEvaluations?.edges?.filter(
                                  e => e.node.needsHitl
                                ).length || 0}
                              </StatNumber>
                            </Stat>
                            <Stat textAlign="center">
                              <StatLabel>Overall Quality</StatLabel>
                              <StatNumber>
                                {selectedRecord?.structuredData?.humanReview?.overallGrade 
                                  ? `${selectedRecord.structuredData.humanReview.overallGrade}/5`
                                  : 'Not Reviewed'}
                              </StatNumber>
                            </Stat>
                          </SimpleGrid>
                        </CardBody>
                      </Card>

                      {/* Reprocessing History */}
                      {(selectedRecord?.structuredData?.reprocessedFrom || selectedRecord?.structuredData?.reprocessedTo) && (
                        <Card bg="gray.700" borderColor="gray.600">
                          <CardHeader>
                            <Heading size="md" color="cyan.300">Reprocessing History</Heading>
                          </CardHeader>
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              {selectedRecord?.structuredData?.reprocessedFrom && (
                                <Box>
                                  <Text fontSize="sm" color="gray.400" mb={1}>Original Shift</Text>
                                  <HStack>
                                    <Text fontWeight="bold">
                                      #{selectedRecord.structuredData.reprocessedFrom}
                                    </Text>
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      variant="link"
                                      onClick={() => {
                                        const originalRecord = shiftRecords?.find(
                                          r => r.id === selectedRecord.structuredData.reprocessedFrom
                                        );
                                        if (originalRecord) {
                                          handleViewRecord(originalRecord);
                                        }
                                      }}
                                    >
                                      View Original
                                    </Button>
                                  </HStack>
                                </Box>
                              )}
                              
                              {selectedRecord?.structuredData?.reprocessedTo && (
                                <Box>
                                  <Text fontSize="sm" color="gray.400" mb={1}>Reprocessed As</Text>
                                  <HStack>
                                    <Text fontWeight="bold">
                                      #{selectedRecord.structuredData.reprocessedTo}
                                    </Text>
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      variant="link"
                                      onClick={() => {
                                        const reprocessedRecord = shiftRecords?.find(
                                          r => r.id === selectedRecord.structuredData.reprocessedTo
                                        );
                                        if (reprocessedRecord) {
                                          handleViewRecord(reprocessedRecord);
                                        }
                                      }}
                                    >
                                      View Reprocessed
                                    </Button>
                                  </HStack>
                                </Box>
                              )}
                            </VStack>
                          </CardBody>
                        </Card>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Tab 2: Patient Evaluations */}
                  <TabPanel>
                    <Box p={4}>
                      {(() => {
                        const aiEvaluation = parseAIEvaluation(selectedRecord);
                        
                        if (!aiEvaluation) {
                          return (
                            <Alert status="warning">
                              <AlertIcon />
                              <AlertTitle>No AI Evaluation Data Available</AlertTitle>
                              <AlertDescription>
                                The AI evaluation has not been completed for this shift yet.
                              </AlertDescription>
                            </Alert>
                          );
                        }

                        const shiftData = aiEvaluation.shift;
                        const studentReport = aiEvaluation.student_report;
                        const perPatientFeedback = studentReport?.per_patient_feedback || [];
                        const detailedFieldReviews = aiEvaluation.detailed_field_reviews?.patients || [];

                        if (perPatientFeedback.length === 0) {
                          return (
                            <Alert status="info">
                              <AlertIcon />
                              <AlertTitle>No Patient Evaluations</AlertTitle>
                              <AlertDescription>
                                No per-patient feedback found in the AI evaluation.
                              </AlertDescription>
                            </Alert>
                          );
                        }

                        return (
                          <VStack spacing={6} align="stretch">
                            {/* Overall Student Feedback Card */}
                            <Card bg="gray.700" borderColor="gray.600">
                              <CardHeader bg="blue.900" borderBottom="1px" borderColor="gray.600">
                                <Heading size="md" color="white">Overall Student Feedback</Heading>
                              </CardHeader>
                              <CardBody>
                                <VStack spacing={4} align="stretch">
                                  {studentReport?.overall_feedback && (
                                    <Box>
                                      <Text fontSize="sm" color="gray.400" mb={2}>Overall Feedback</Text>
                                      <Text>{studentReport.overall_feedback}</Text>
                                    </Box>
                                  )}
                                  
                                  {studentReport?.strengths?.length > 0 && (
                                    <Box>
                                      <Text fontSize="sm" color="gray.400" mb={2}>Strengths</Text>
                                      <VStack align="stretch" spacing={1}>
                                        {studentReport.strengths.map((strength, idx) => (
                                          <HStack key={idx}>
                                            <CheckIcon color="green.400" boxSize={3} />
                                            <Text fontSize="sm">{strength}</Text>
                                          </HStack>
                                        ))}
                                      </VStack>
                                    </Box>
                                  )}
                                  
                                  {studentReport?.growth_opportunities?.length > 0 && (
                                    <Box>
                                      <Text fontSize="sm" color="gray.400" mb={2}>Growth Opportunities</Text>
                                      <VStack align="stretch" spacing={1}>
                                        {studentReport.growth_opportunities.map((opportunity, idx) => (
                                          <HStack key={idx}>
                                            <StarIcon color="yellow.400" boxSize={3} />
                                            <Text fontSize="sm">{opportunity}</Text>
                                          </HStack>
                                        ))}
                                      </VStack>
                                    </Box>
                                  )}
                                  
                                  {studentReport?.protocol_reference && (
                                    <Box>
                                      <Text fontSize="sm" color="gray.400" mb={2}>Protocol Reference</Text>
                                      <Text fontSize="sm" fontStyle="italic">{studentReport.protocol_reference}</Text>
                                    </Box>
                                  )}
                                </VStack>
                              </CardBody>
                            </Card>

                            {/* Per-Patient Feedback Grid */}
                            <SimpleGrid columns={2} spacing={4}>
                              {perPatientFeedback.map((patient, idx) => {
                                const patientId = patient.patient_id;
                                const flagKey = `patient_${patientId}_disagreement`;
                                const isFlagged = patientFlags[patientId];
                                
                                return (
                                  <Card key={patientId} bg="gray.700" borderColor="gray.600">
                                    <CardHeader bg="purple.900" borderBottom="1px" borderColor="gray.600">
                                      <VStack align="stretch" spacing={2}>
                                        <Heading size="sm" color="white">
                                          Patient {patientId}
                                        </Heading>
                                        
                                        {/* Flag for Review Checkbox */}
                                        <Checkbox
                                          colorScheme="red"
                                          isChecked={isFlagged}
                                          onChange={(e) => {
                                            setPatientFlags(prev => ({
                                              ...prev,
                                              [patientId]: e.target.checked
                                            }));
                                            if (!e.target.checked) {
                                              // Clear the disagreement comment when unchecking
                                              setReviewData(prev => {
                                                const newFieldOverrides = { ...prev.fieldOverrides };
                                                delete newFieldOverrides[flagKey];
                                                return {
                                                  ...prev,
                                                  fieldOverrides: newFieldOverrides
                                                };
                                              });
                                            }
                                          }}
                                        >
                                          <Text fontSize="sm" color="white">Flag for Review / Disagree with AI</Text>
                                        </Checkbox>
                                        
                                        {/* Disagreement Textarea */}
                                        {isFlagged && (
                                          <Textarea
                                            placeholder="Explain why you disagree with the AI assessment..."
                                            size="sm"
                                            rows={2}
                                            bg="gray.600"
                                            borderColor="gray.500"
                                            value={reviewData.fieldOverrides[flagKey]?.comment || ''}
                                            onChange={(e) => {
                                              setReviewData(prev => ({
                                                ...prev,
                                                fieldOverrides: {
                                                  ...prev.fieldOverrides,
                                                  [flagKey]: {
                                                    comment: e.target.value,
                                                    timestamp: new Date().toISOString(),
                                                    reviewerId: user?.id
                                                  }
                                                }
                                              }));
                                            }}
                                          />
                                        )}
                                      </VStack>
                                    </CardHeader>
                                    <CardBody>
                                      <VStack spacing={3} align="stretch">
                                        {/* Clinical Summary */}
                                        {patient.clinical_summary && (
                                          <Box>
                                            <Text fontSize="xs" color="gray.400" mb={1}>Clinical Summary</Text>
                                            <Text fontSize="sm">{patient.clinical_summary}</Text>
                                          </Box>
                                        )}
                                        
                                        {/* Documentation Feedback */}
                                        {patient.documentation_feedback && (
                                          <Box>
                                            <Text fontSize="xs" color="gray.400" mb={1}>Documentation</Text>
                                            <Text fontSize="sm">{patient.documentation_feedback}</Text>
                                          </Box>
                                        )}
                                        
                                        {/* Clinical Reasoning */}
                                        {patient.clinical_reasoning_feedback && (
                                          <Box>
                                            <Text fontSize="xs" color="gray.400" mb={1}>Clinical Reasoning</Text>
                                            <Text fontSize="sm">{patient.clinical_reasoning_feedback}</Text>
                                          </Box>
                                        )}
                                        
                                        {/* Commendations */}
                                        {patient.commendations?.length > 0 && (
                                          <Box>
                                            <Text fontSize="xs" color="gray.400" mb={1}>Commendations</Text>
                                            <VStack align="stretch" spacing={1}>
                                              {patient.commendations.map((item, i) => (
                                                <HStack key={i}>
                                                  <CheckIcon color="green.400" boxSize={3} />
                                                  <Text fontSize="xs">{item}</Text>
                                                </HStack>
                                              ))}
                                            </VStack>
                                          </Box>
                                        )}
                                        
                                        {/* Improvement Suggestions */}
                                        {patient.improvement_suggestions?.length > 0 && (
                                          <Box>
                                            <Text fontSize="xs" color="gray.400" mb={1}>Improvement Areas</Text>
                                            <VStack align="stretch" spacing={1}>
                                              {patient.improvement_suggestions.map((item, i) => (
                                                <HStack key={i}>
                                                  <WarningIcon color="yellow.400" boxSize={3} />
                                                  <Text fontSize="xs">{item}</Text>
                                                </HStack>
                                              ))}
                                            </VStack>
                                          </Box>
                                        )}
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                );
                              })}
                            </SimpleGrid>
                          </VStack>
                        );
                      })()}
                    </Box>
                  </TabPanel>

                  {/* Tab 3: Instructor Review */}
                  <TabPanel>
                    <Box p={4}>
                      {/* Overall Review Form */}
                      <VStack align="stretch" spacing={4} maxW="600px" mx="auto">
                        <Card bg="gray.700" borderColor="gray.600">
                          <CardHeader>
                            <Heading size="md" color="green.300">Overall Review</Heading>
                          </CardHeader>
                          <CardBody>
                            <VStack spacing={4} align="stretch">
                              {/* Audit Verdict */}
                              <FormControl>
                                <FormLabel>Audit Verdict</FormLabel>
                                <Select
                                  value={reviewData.auditVerdict}
                                  onChange={(e) => setReviewData(prev => ({
                                    ...prev,
                                    auditVerdict: e.target.value
                                  }))}
                                  bg="gray.600"
                                  borderColor="gray.500"
                                >
                                  <option value="">Select verdict...</option>
                                  <option value="pass">Pass</option>
                                  <option value="fail">Fail</option>
                                  <option value="needs_review">Needs Review</option>
                                </Select>
                              </FormControl>

                              {/* Overall Grade */}
                              <FormControl>
                                <FormLabel>Overall Grade (1-5 stars)</FormLabel>
                                <StarRating
                                  value={reviewData.overallGrade}
                                  onChange={(value) => setReviewData(prev => ({
                                    ...prev,
                                    overallGrade: value
                                  }))}
                                />
                              </FormControl>

                              {/* Review Notes */}
                              <FormControl>
                                <FormLabel>General Review Notes</FormLabel>
                                <Textarea
                                  value={reviewData.reviewNotes}
                                  onChange={(e) => setReviewData(prev => ({
                                    ...prev,
                                    reviewNotes: e.target.value
                                  }))}
                                  rows={10}
                                  bg="gray.600"
                                  borderColor="gray.500"
                                  placeholder="Enter overall feedback for the student..."
                                />
                              </FormControl>

                              {/* Submit Button */}
                              <Button
                                colorScheme={reviewData.auditVerdict ? "green" : "blue"}
                                size="lg"
                                leftIcon={<CheckIcon />}
                                onClick={handleSubmitReview}
                                isLoading={updating}
                                loadingText="Submitting..."
                              >
                                {reviewData.auditVerdict ? 'Submit Review' : 'Save Draft'}
                              </Button>
                            </VStack>
                          </CardBody>
                        </Card>
                      </VStack>
                    </Box>
                  </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Box>
              </HStack>
            </ModalBody>

            <ModalFooter borderTop="1px" borderColor="gray.700">
              <HStack spacing={3}>
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
                
                <Button
                  colorScheme="blue"
                  leftIcon={<CheckIcon />}
                  onClick={handleSubmitReview}
                  isLoading={updating}
                  loadingText="Submitting..."
                >
                  {reviewData.auditVerdict ? 'Submit Review' : 'Save Draft'}
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Reprocess Confirmation Dialog */}
        <AlertDialog
          isOpen={isReprocessDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onReprocessDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent bg="gray.800" color="white">
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Reprocess Shift
              </AlertDialogHeader>

              <AlertDialogBody>
                This will create a new shift processing record with current timestamp and re-run the entire evaluation pipeline. The original shift will remain unchanged but will be linked to the new processing. Continue?
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button 
                  ref={cancelRef} 
                  onClick={onReprocessDialogClose} 
                  variant="ghost"
                  isDisabled={reprocessing}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="orange"
                  onClick={handleReprocessShift}
                  ml={3}
                  isLoading={reprocessing}
                  loadingText="Reprocessing..."
                  isDisabled={reprocessing}
                >
                  Confirm Reprocess
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>
    </Box>
  );
};

export default HITLReview;
